import React, { useEffect, useState } from "react";
import {
  deleteBurpSuiteScan,
  getBurpSuiteScanDetailsByScanID,
  getScanDataByUsername,
  sendBurpSuiteScanByEmail,
} from "../../../services/webServices";
import {
  Box,
  Button,
  Grid2,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ReactComponent as EmailIcon } from "../../../images/EmailIcon.svg";
import { ReactComponent as EyeIcon } from "../../../images/EyeIcon.svg";
import { ReactComponent as RedDownloadIcon } from "../../../images/RedDownloadIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../images/DeleteIcon.svg";
import { ReactComponent as Group18101 } from "../../../images/Group18101.svg";
import { ReactComponent as Ellipse615 } from "../../../images/Ellipse615.svg";
import { ReactComponent as Pause } from "../../../images/Pause.svg";
import { ReactComponent as Failed } from "../../../images/Failed.svg";
import { ReactComponent as ProgressRun } from "../../../images/ProgressRun.svg";
import { ReactComponent as CompletedStatusBar } from "../../../images/CompletedStatusBar.svg";
import MessagePopup from "../../popup-components/MessagePopup";
import { useNavigate } from "react-router-dom";
import BurpSuiteViewDashboard from "./BurpSuiteViewDashboard";
import CommonDownload from "../../custom-components/CommonDownload";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";

const tableHeaderCellStyle = {
  backgroundColor: "#0085DE",
  color: "white",
  border: "1px solid #ddd",
  fontSize: "14px",
};

const tableCellStyle = {
  border: "1px solid #ddd",
  fontSize: "14px",
};

const BurpSuiteScansComponent = () => {
  const navigate = useNavigate();
  const [scanIDResults, setScanIDResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [viewDashboard, SetViewDashboard] = useState(false);
  const [viewScanDetails, setViewScanDetails] = useState([]);
  const [webURL, setWebURL] = useState("");
  const [openDownload, setOpenDownload] = useState(false);
  const [downloadData, setDownloadData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getScanDataByUsername();
        setScanIDResults(response.data.scans || []);
      } catch (error) {
        console.error("Error fetching scan data:", error);
      }
    };

    fetchData();
  }, []);

  // Handle send email
  // This function will send the scan data by email and show a success or error message
  // It will also set the openErrorPopup state to true to show the popup
  // The scanID is passed as an argument to identify which scan to send
  const handleSendEmail = async (scanID) => {
    try {
      const sendEmail = await sendBurpSuiteScanByEmail(scanID);
      setOpenErrorPopup(true);
      setErrorMessage("Email sent successfully.");
    } catch (error) {
      console.error("Error sending email:", error);
      setOpenErrorPopup(true);
      setErrorMessage("Failed to send email. Please try again.");
    }
  };

  const handleInitiateNewScan = async () => {
    navigate("/dashboard/security-testing/dast-phase");
  };

  // Handle view scan details
  // This function will fetch the scan details and set the viewScanDetails state
  // It will also set the webURL state and toggle the viewDashboard state
  // to show the BurpSuiteViewDashboard component
  const handleViewScan = async ({ scanID, url }) => {
    SetViewDashboard(false);
    try {
      const viewScanResult = await getBurpSuiteScanDetailsByScanID(scanID);
      console.log("Scan Details:", viewScanResult.data);
      setWebURL(url || "");
      setViewScanDetails(viewScanResult.data?.issues);
      SetViewDashboard(true);
    } catch (error) {
      console.error("Error viewing scan details:", error);
      setOpenErrorPopup(true);
      setErrorMessage("Failed to view scan details. Please try again.");
    }
  };

  // Handle download scan
  // This function will fetch the scan details and set the download data
  // It will also open the download popup
  const handleDownloadScan = async (scanID) => {
    // setDownloadData([]);
    setOpenDownload(true);
    try {
      const downloadScanResult = await getBurpSuiteScanDetailsByScanID(scanID);
      console.log("Download Scan Details:", downloadScanResult.data?.issues);
      setDownloadData(downloadScanResult.data?.issues || []);
    } catch (error) {
      console.error("Error downloading scan:", error);
      setOpenErrorPopup(true);
      setErrorMessage("Failed to download scan. Please try again.");
    }
  };

  // Handle delete scan
  // This function will delete the scan and update the state accordingly
  const handleDeleteScan = async (scanID) => {
    try {
      const deleteScanID = await deleteBurpSuiteScan(scanID);
      setScanIDResults((prevResults) =>
        prevResults.filter((item) => item.scan_id !== scanID)
      );
      setOpenErrorPopup(true);
      setErrorMessage("Scan deleted successfully.");
    } catch (error) {
      console.error("Error deleting scan:", error);
      setOpenErrorPopup(true);
      setErrorMessage("Failed to delete scan. Please try again.");
    }
  };

  // Add safety checks to ensure downloadData is always an array
  const formattedScanResult = (Array.isArray(downloadData) ? downloadData : [])
    .filter((scanResult) => scanResult && scanResult.severity !== "info")
    .map((scanResult) => {
      // Add safety check for scanResult
      if (!scanResult || typeof scanResult !== "object") {
        return null;
      }

      const flattened = {};

      Object.entries(scanResult).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          flattened[key] = flattenArray(value); // Your flattenArray logic
        } else if (typeof value === "object" && value !== null) {
          flattened[key] = flattenObject(value); // Your flattenObject logic
        } else {
          flattened[key] = value;
        }
      });

      return {
        Title: flattened.name || "",
        Description: flattened.description || "",
        Severity: flattened.severity || "",
        Path: (flattened.origin || "") + (flattened.path || ""),
        References: flattened.issue_background || "",
        "Recommended Solutions": flattened.remediation_background || "",
      };
    })
    .filter((item) => item !== null); // Remove null items from failed mappings

  const tableDataConverter = (scanResults) => {
    if (!Array.isArray(scanResults) || scanResults.length === 0) {
      return { headers: [], values: [] }; // ✅ safe fallback
    }

    const headers = Object.keys(scanResults[0]);
    const values = scanResults.map((eachScanResult) => {
      return Object.values(eachScanResult);
    });

    return { headers, values };
  };

  return (
    <Box p={3} sx={{ backgroundColor: "#f6f3f2", minHeight: "100vh" }}>
      <Box
        sx={{
          height: { xs: "auto", md: "15vh" },
          backgroundColor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          paddingY: 2,
          borderRadius: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            width: "100%",
            gap: 2,
            p: 4,
          }}
        >
          {/* Left: Title & Subtitle */}
          <Box>
            <Typography
              sx={{
                fontSize: "27px",
                fontWeight: 700,
              }}
            >
              Scan Status Dashboard
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#AE241E",
                fontWeight: 600,
                mt: 2,
              }}
            >
              Scan initiated, please find the below status
            </Typography>
          </Box>

          {/* Right: Search & Refresh Button */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                borderRadius: "12px",
                minWidth: "200px",
              }}
            /> */}
            <Button
              sx={{
                backgroundColor: "#1D4FD9",
                fontSize: "15px",
                fontWeight: 600,
                borderRadius: "12px",
                color: "#FFF",
                height: "42px",
                width: "148px",
              }}
              onClick={handleInitiateNewScan}
            >
              New Scan
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()} // 🆕 Add this line
              sx={{
                height: 42,
                borderRadius: "30px",
                backgroundColor: "#F3F7FB",
                color: "#2B5185",
                textTransform: "none",
                boxShadow: "none",
                border: "1px solid #D0D7DE",
                "&:hover": {
                  backgroundColor: "#E8EFF6",
                  boxShadow: "none",
                },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "70vh",
          overflowY: "auto",
          mt: 6,
          borderRadius: "16px",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderCellStyle}>S.No</TableCell>
              <TableCell sx={tableHeaderCellStyle}>URL</TableCell>
              <TableCell sx={tableHeaderCellStyle}>SCAN ID</TableCell>
              <TableCell sx={tableHeaderCellStyle}>PROGRESS</TableCell>
              <TableCell sx={tableHeaderCellStyle}>CREATED AT</TableCell>
              <TableCell sx={tableHeaderCellStyle}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {scanIDResults.length > 0 ? (
              scanIDResults.map((item, index) => (
                <TableRow key={item.task_id || index}>
                  <TableCell sx={tableCellStyle}>{index + 1}</TableCell>
                  <TableCell sx={tableCellStyle}>{item.url}</TableCell>
                  <TableCell sx={tableCellStyle}>{item.scan_id}</TableCell>
                  <TableCell sx={tableCellStyle}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center", // vertically center all elements
                        gap: 1.5, // spacing between children (1.5 * 8 = 12px)
                      }}
                    >
                      {/* Progress percentage */}
                      <Typography sx={{ fontWeight: 500 }}>
                        {item.progress}%
                      </Typography>

                      {/* Your custom progress component */}
                      {item.progress >= 100 ? (
                        <CompletedStatusBar />
                      ) : (
                        <ProgressRun />
                      )}

                      {/* Status pill */}
                      <Box
                        sx={{
                          backgroundColor:
                            item.progress >= 100 ? "#D1FAE5" : "#DBEAFE",
                          width: "75px",
                          height: "20px",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          color: item.progress >= 100 ? "#2C5B5A" : "#2656C4", // Optional: adjust for contrast
                        }}
                      >
                        {item.progress >= 100 ? "Complete" : "RUNNING"}
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={tableCellStyle}>
                    {new Date(item.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      {/* <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleSendEmail(item.scan_id)}
                      >
                        <EmailIcon />
                      </Box> */}
                      <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          handleViewScan({
                            scanID: item.scan_id,
                            url: item.url,
                          })
                        }
                      >
                        <EyeIcon />
                      </Box>
                      <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          handleDownloadScan(item.scan_id);
                        }}
                      >
                        <RedDownloadIcon />
                      </Box>
                      <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleDeleteScan(item.scan_id)}
                      >
                        <DeleteIcon />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={tableCellStyle}>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                    }}
                  >
                    {" "}
                    No scan data found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* <Grid2 sx={{ display: "flex", mt: 4, mb: 4 }}>
        <Button
          sx={{
            backgroundColor: "#1D4FD9",
            fontSize: "15px",
            fontWeight: 600,
            borderRadius: "12px",
            color: "#FFF",
            height: "50px",
            width: "148px",
          }}
          onClick={handleInitiateNewScan}
        >
          New Scan
        </Button>
        <Button
          sx={{
            backgroundColor: "#374559",
            fontSize: "15px",
            fontWeight: 600,
            color: "#FFF",
            borderRadius: "12px",
            height: "50px",
            width: "224px",
            ml: 4,
          }}
        >
          Export All Reports
        </Button>
        <Button
          sx={{
            backgroundColor: "#E7B982",
            fontSize: "16px",
            fontWeight: 600,
            
            color: "#FFF",
            borderRadius: "12px",
            height: "66px",
            width: "206px",
            ml: 4,
          }}
        >
          Pause Scan
        </Button>
        <Button
          sx={{
            backgroundColor: "#DE2929",
            fontSize: "16px",
            fontWeight: 600,
            
            color: "#FFF",
            borderRadius: "12px",
            height: "66px",
            width: "208px",
            ml: 4,
          }}
        >
          Stop All Scans
        </Button>
        <Button
          sx={{
            ml: 3,
            backgroundColor: "#7BC7B4",
            fontWeight: 600,
            height: "50px",
            color: "#FFF",
            borderRadius: "12px",
            fontSize: "15px",
            width: "224px",
          }}
        >
          Download Selected
        </Button>
      </Grid2> */}

      <MessagePopup
        modalOpen={openErrorPopup}
        okayHandler={() => setOpenErrorPopup(false)}
        message={errorMessage}
      />

      {viewDashboard && (
        <BurpSuiteViewDashboard
          dastScanData={viewScanDetails}
          webURL={webURL}
        />
      )}

      <CommonDownload
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        serviceName="Active DAST Scan Report"
        serviceResponse={[formattedScanResult]}
        wordData={[
          {
            heading: "Active DAST Scan Report",
            type: "table",
            data: { ...tableDataConverter(formattedScanResult) }, // ✅ now always safe
          },
        ]}
        documents={["docx", "xls", "csv"]}
      />
    </Box>
  );
};

export default BurpSuiteScansComponent;
